import React, { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import { parseSerialData, ParsedSerialData, isStableWeight } from '../utils/serialParsing';
import {
  isAndroidDevice,
  SERIAL_UNSUPPORTED_ANDROID,
  SERIAL_UNSUPPORTED_DESKTOP,
  ANDROID_PAIRING_HINT,
} from '../utils/serialPlatform';
import { addLogBalanza } from '../utils/balanzaLog';

// Re-export for backwards compatibility
export type { ParsedSerialData } from '../utils/serialParsing';
export { parseSerialData } from '../utils/serialParsing';

// Type alias for backwards compatibility
type SerialDeviceData = ParsedSerialData;

// Serial device types
interface SerialPort {
  readable: ReadableStream | null;
  writable: WritableStream | null;
  open(options: SerialOptions): Promise<void>;
  close(): Promise<void>;
  /** En puertos BT suele devolver {} — ese dato también es diagnóstico. */
  getInfo?(): { usbVendorId?: number; usbProductId?: number };
}

interface SerialOptions {
  baudRate: number;
  dataBits?: 7 | 8;
  stopBits?: 1 | 2;
  parity?: 'none' | 'even' | 'odd';
  flowControl?: 'none' | 'hardware';
}

interface Serial extends EventTarget {
  requestPort(): Promise<SerialPort>;
  getPorts(): Promise<SerialPort[]>;
}

declare global {
  interface Navigator {
    serial?: Serial;
  }
}

interface UseSerialDevicesOptions {
  onAnimalIdReceived?: (id: string) => void;
  onWeightReceived?: (weight: number) => void;
  onWeightUpdate?: (weight: number, status?: string) => void;
  onDataReceived?: (data: SerialDeviceData) => void;
  autoRequestWeight?: boolean;
  requestInterval?: number;
}

interface DeviceContextType {
  // Serial device state and methods
  isSerialConnected: boolean;
  serialError: string | null;
  connectSerialDevice: () => Promise<void>;
  disconnectSerialDevice: () => Promise<void>;
  sendSerialCommand: (command: string) => Promise<boolean>;

  // Serial device options
  setSerialOptions: (options: UseSerialDevicesOptions) => void;

  // SOLUCIÓN 2: Método para limpiar timeouts de reinicio
  clearRestartTimeouts: () => void;
}

const DeviceContext = createContext<DeviceContextType | undefined>(undefined);

interface DeviceProviderProps {
  children: ReactNode;
}

export const DeviceProvider: React.FC<DeviceProviderProps> = ({ children }) => {
  // Serial device state (persisted across component remounts)
  const [isSerialConnected, setIsSerialConnected] = useState(false);
  const [serialError, setSerialError] = useState<string | null>(null);
  const [serialOptions, setSerialOptions] = useState<UseSerialDevicesOptions>({});

  // Refs to persist across re-renders
  const portRef = useRef<SerialPort | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader | null>(null);
  const writerRef = useRef<WritableStreamDefaultWriter | null>(null);
  const bufferRef = useRef<string>('');
  const requestIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const multipleIdsTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastIdBufferRef = useRef<string[]>([]);

  // SOLUCIÓN 2: Ref para los callbacks que siempre apunta a la versión más reciente
  const callbacksRef = useRef<UseSerialDevicesOptions>({});

  // SOLUCIÓN 1: Flag para prevenir múltiples instancias de readFromPort
  const isReadingRef = useRef(false);

  // SOLUCIÓN 3: Ref para limpiar timeouts antiguos
  const readFromPortTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // SOLUCIÓN 4: Contador de reinicios con límite
  const restartCountRef = useRef(0);
  const MAX_RESTARTS = 5;

  // Watchdog de silencio: N timeouts de lectura seguidos (de 5s) con el
  // polling {RW} activo = la balanza no responde. Antes el loop era mudo y
  // el usuario quedaba en "✓ Conectado" para siempre sin peso ni error
  // (síntoma exacto reportado en celulares Android).
  const consecutiveTimeoutsRef = useRef(0);
  const silenceWarnedRef = useRef(false);
  const READ_TIMEOUTS_SILENCIO = 3;
  const SILENCIO_ERROR =
    'Conectado, pero la balanza no responde. Verificá que esté encendida y cerca; si sigue, abrí el Diagnóstico de balanza.';

  // Diagnostic: Track provider instance
  const instanceId = useRef(`DeviceProvider-${Date.now()}`);
  console.log(`[${instanceId.current}] DeviceProvider initialized`);

  /**
   * Send command to serial device
   */
  const sendSerialCommand = useCallback(async (command: string): Promise<boolean> => {
    if (!writerRef.current) {
      console.log(`[${instanceId.current}] No writer available for sending command`);
      return false;
    }

    try {
      console.log(`[${instanceId.current}] Sending command:`, JSON.stringify(command));
      const encoder = new TextEncoder();
      await writerRef.current.write(encoder.encode(command));
      addLogBalanza('TX', `${JSON.stringify(command)} → ok`);
      return true;
    } catch (err) {
      const raw = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      // Crítico: un write que falla (típico RFCOMM/BT en Android) antes era
      // invisible — la balanza nunca recibía los {RW} y nadie se enteraba.
      addLogBalanza('ERROR', `Fallo el envío de ${JSON.stringify(command)} → ${raw}`);
      console.error(`[${instanceId.current}] Error sending command:`, err);
      return false;
    }
  }, []);

  /**
   * Process buffered serial data
   * SOLUCIÓN 2: Ahora usa callbacksRef en lugar de serialOptions como dependencia
   * Esto previene que processBuffer se recree cuando serialOptions cambia
   */
  const processBuffer = useCallback(() => {
    console.log(`[${instanceId.current}] Processing buffer:`, JSON.stringify(bufferRef.current));
    const normalizedBuffer = bufferRef.current.replace(/\r/g, '\n');
    const lines = normalizedBuffer.split('\n');
    const lastChunk = lines.pop();

    // Usar callbacksRef para obtener siempre los callbacks más recientes
    const callbacks = callbacksRef.current;

    for (const line of lines) {
      if (line.trim()) {
        if (line.includes('[') && line.includes(']')) {
          const parsedData = parseSerialData(line);

          if (parsedData.type === 'animal_id') {
            lastIdBufferRef.current.push(parsedData.value);

            if (multipleIdsTimerRef.current) {
              clearTimeout(multipleIdsTimerRef.current);
            }

            multipleIdsTimerRef.current = setTimeout(() => {
              if (lastIdBufferRef.current.length > 0) {
                const lastId = lastIdBufferRef.current[lastIdBufferRef.current.length - 1];
                if (callbacks.onAnimalIdReceived) {
                  callbacks.onAnimalIdReceived(lastId);
                }
                lastIdBufferRef.current = [];
              }
              multipleIdsTimerRef.current = null;
            }, 500);
          } else if (parsedData.type === 'weight') {
            const weight = parseFloat(parsedData.value);
            if (!isNaN(weight)) {
              if (callbacks.onWeightUpdate) {
                callbacks.onWeightUpdate(weight, parsedData.status);
              }
              if (callbacks.onWeightReceived && isStableWeight(parsedData.status)) {
                callbacks.onWeightReceived(weight);
              }
            }
          }
        }
      }
    }

    if (lastChunk) {
      const openBracketCount = (lastChunk.match(/\[/g) || []).length;
      const closeBracketCount = (lastChunk.match(/\]/g) || []).length;

      if (openBracketCount > closeBracketCount) {
        bufferRef.current = lastChunk;
      } else if (lastChunk.trim()) {
        if (lastChunk.includes('[') && lastChunk.includes(']')) {
          const parsedData = parseSerialData(lastChunk);

          if (parsedData.type === 'animal_id') {
            lastIdBufferRef.current.push(parsedData.value);

            if (multipleIdsTimerRef.current) {
              clearTimeout(multipleIdsTimerRef.current);
            }

            multipleIdsTimerRef.current = setTimeout(() => {
              if (lastIdBufferRef.current.length > 0) {
                const lastId = lastIdBufferRef.current[lastIdBufferRef.current.length - 1];
                if (callbacks.onAnimalIdReceived) {
                  callbacks.onAnimalIdReceived(lastId);
                }
                lastIdBufferRef.current = [];
              }
              multipleIdsTimerRef.current = null;
            }, 500);
          } else if (parsedData.type === 'weight') {
            const weight = parseFloat(parsedData.value);
            if (!isNaN(weight)) {
              if (callbacks.onWeightUpdate) {
                callbacks.onWeightUpdate(weight, parsedData.status);
              }
              if (callbacks.onWeightReceived && isStableWeight(parsedData.status)) {
                callbacks.onWeightReceived(weight);
              }
            }
          }
        }
        bufferRef.current = '';
      } else {
        bufferRef.current = '';
      }
    } else {
      bufferRef.current = '';
    }
  }, []); // SOLUCIÓN 2: Sin dependencias - no se recrea más

  /**
   * Read from serial port
   * SOLUCIÓN 1: Previene múltiples instancias ejecutándose simultáneamente
   * SOLUCIÓN 3: Limpia timeouts antiguos antes de crear nuevos
   * SOLUCIÓN 4: Limita el número de reinicios para evitar bucles infinitos
   */
  const readFromPort = useCallback(async () => {
    // SOLUCIÓN 1: Prevenir múltiples instancias
    if (isReadingRef.current) {
      console.log(`[${instanceId.current}] Already reading, skipping duplicate call...`);
      return;
    }

    if (!portRef.current) {
      console.log(`[${instanceId.current}] No port available for reading`);
      return;
    }

    console.log(`[${instanceId.current}] Starting to read from port...`);
    isReadingRef.current = true; // Marcar que estamos leyendo

    try {
      const reader = portRef.current.readable?.getReader();
      if (!reader) {
        throw new Error('Unable to get reader from port');
      }

      readerRef.current = reader;
      const decoder = new TextDecoder();
      console.log(`[${instanceId.current}] Reader acquired, waiting for data...`);

      // Reiniciar contador cuando comenzamos a leer exitosamente
      restartCountRef.current = 0;

      // Fix readPromise huérfana: la MISMA promesa de lectura se reutiliza
      // entre timeouts — antes, cuando el timeout ganaba la race, el read
      // viejo quedaba vivo y su chunk se descartaba al resolver tarde.
      let pendingRead: Promise<ReadableStreamReadResult<Uint8Array>> | null = null;

      while (true) {
        let value: Uint8Array | undefined;
        let done: boolean = false;
        if (!pendingRead) {
          pendingRead = reader.read();
          // Guard anti-unhandledrejection si el puerto se cierra mientras
          // esperamos el timeout (la race lo atrapa en la vuelta siguiente).
          pendingRead.catch(() => {});
        }
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        const timeoutPromise = new Promise<never>((_, reject) => {
          timeoutId = setTimeout(() => reject(new Error('Read timeout')), 5000);
        });
        try {
          const result = await Promise.race([pendingRead, timeoutPromise]);
          pendingRead = null; // recién acá se consume
          value = result.value;
          done = result.done;
        } catch (err) {
          if ((err as Error).message === 'Read timeout') {
            // Watchdog de silencio (antes: `continue` mudo).
            consecutiveTimeoutsRef.current++;
            addLogBalanza('WARN', `Sin datos en 5s (timeout de lectura, ${consecutiveTimeoutsRef.current} seguidos)`);
            if (
              consecutiveTimeoutsRef.current >= READ_TIMEOUTS_SILENCIO &&
              writerRef.current &&
              !silenceWarnedRef.current
            ) {
              silenceWarnedRef.current = true;
              addLogBalanza(
                'WARN',
                `Conectado pero sin RX tras ${consecutiveTimeoutsRef.current * 5}s con el polling {RW} activo — la balanza no responde`
              );
              setSerialError(SILENCIO_ERROR);
            }
            continue; // pendingRead se reutiliza en la próxima vuelta
          } else {
            throw err;
          }
        } finally {
          if (timeoutId) clearTimeout(timeoutId);
        }

        if (done) {
          console.log(`[${instanceId.current}] Reader stream ended`);
          addLogBalanza('WARN', 'El stream de lectura terminó (done)');
          readerRef.current?.releaseLock();
          readerRef.current = null;

          // SOLUCIÓN 4: Verificar límite de reinicios
          if (portRef.current?.readable) {
            restartCountRef.current++;
            if (restartCountRef.current > MAX_RESTARTS) {
              console.error(`[${instanceId.current}] Max restarts (${MAX_RESTARTS}) reached, stopping...`);
              addLogBalanza('ERROR', `Límite de reinicios del reader (${MAX_RESTARTS}) alcanzado`);
              setSerialError('Se alcanzó el límite de reinicios automáticos. Reconectá el dispositivo.');
              isReadingRef.current = false;
              break;
            }
            console.log(`[${instanceId.current}] Port still open, restarting reader... (${restartCountRef.current}/${MAX_RESTARTS})`);
            addLogBalanza('INFO', `Puerto sigue abierto, reiniciando reader (${restartCountRef.current}/${MAX_RESTARTS})`);
            // SOLUCIÓN 3: Limpiar timeout anterior antes de crear uno nuevo
            if (readFromPortTimeoutRef.current) {
              clearTimeout(readFromPortTimeoutRef.current);
            }
            readFromPortTimeoutRef.current = setTimeout(() => {
              isReadingRef.current = false; // Permitir nueva ejecución
              readFromPort();
            }, 100);
          }
          break;
        }

        if (value) {
          const text = decoder.decode(value);
          // RX crudo ANTES de tocar el buffer (mismo criterio que la app mobile).
          addLogBalanza('RX', `crudo: ${JSON.stringify(text)} · largo=${text.length}`);
          console.log(`[${instanceId.current}] Received text:`, JSON.stringify(text));
          consecutiveTimeoutsRef.current = 0;
          if (silenceWarnedRef.current) {
            silenceWarnedRef.current = false;
            addLogBalanza('INFO', 'Datos recibidos de nuevo tras el silencio');
            setSerialError(null);
          }
          bufferRef.current += text;
          processBuffer();
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error reading from port';
      const rawName = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      addLogBalanza('ERROR', `Lectura del puerto falló → ${rawName}`);
      console.error(`[${instanceId.current}] Read error:`, errorMessage);

      if (errorMessage.includes('NetworkError')) {
        // Desconexión BT típica (balanza apagada/fuera de alcance): logueada,
        // pero sin ensuciar el serialError en cada reintento del reader.
        addLogBalanza('WARN', 'NetworkError: se perdió la conexión física con la balanza');
      }

      if (!errorMessage.includes('NetworkError') && !errorMessage.includes('Aborted')) {
        setSerialError(errorMessage);
      }

      // SOLUCIÓN 4: Verificar límite de reinicios también en errores
      if (portRef.current?.readable && !errorMessage.includes('Aborted')) {
        restartCountRef.current++;
        if (restartCountRef.current > MAX_RESTARTS) {
          console.error(`[${instanceId.current}] Max restarts (${MAX_RESTARTS}) reached after error, stopping...`);
          addLogBalanza('ERROR', `Límite de reinicios del reader tras error (${MAX_RESTARTS}) alcanzado`);
          setSerialError('Se alcanzó el límite de reinicios automáticos. Reconectá el dispositivo.');
          isReadingRef.current = false;
          return;
        }
        console.log(`[${instanceId.current}] Attempting to restart reader after error... (${restartCountRef.current}/${MAX_RESTARTS})`);
        addLogBalanza('INFO', `Reiniciando reader tras error (${restartCountRef.current}/${MAX_RESTARTS})`);
        // SOLUCIÓN 3: Limpiar timeout anterior
        if (readFromPortTimeoutRef.current) {
          clearTimeout(readFromPortTimeoutRef.current);
        }
        readFromPortTimeoutRef.current = setTimeout(() => {
          isReadingRef.current = false; // Permitir nueva ejecución
          readFromPort();
        }, 500);
      }
    } finally {
      if (readerRef.current) {
        try {
          readerRef.current?.releaseLock();
        } catch (e) {
          // Ignore errors
        }
        readerRef.current = null;
      }
    }
  }, [processBuffer]);

  /**
   * Start auto weight request
   */
  const startAutoWeightRequest = useCallback(() => {
    if (requestIntervalRef.current) {
      clearInterval(requestIntervalRef.current);
    }

    // SOLUCIÓN 2: Usar callbacksRef en lugar de serialOptions
    const interval = callbacksRef.current.requestInterval || 1000;
    console.log(`[${instanceId.current}] Starting auto weight request every ${interval}ms`);

    requestIntervalRef.current = setInterval(async () => {
      if (writerRef.current) {
        const command = '{RW}\r';
        await sendSerialCommand(command);
      }
    }, interval);
  }, [sendSerialCommand]);

  /**
   * Stop auto weight request
   */
  const stopAutoWeightRequest = useCallback(() => {
    if (requestIntervalRef.current) {
      console.log(`[${instanceId.current}] Stopping auto weight request`);
      clearInterval(requestIntervalRef.current);
      requestIntervalRef.current = null;
    }
  }, []);

  /**
   * SOLUCIÓN 3: Limpiar timeouts de reinicio pendientes
   * Útil para evitar reinicios múltiples cuando se reconecta manualmente
   */
  const clearRestartTimeouts = useCallback(() => {
    if (readFromPortTimeoutRef.current) {
      console.log(`[${instanceId.current}] Clearing pending restart timeout`);
      clearTimeout(readFromPortTimeoutRef.current);
      readFromPortTimeoutRef.current = null;
    }
    // Resetear el contador de reinicios
    restartCountRef.current = 0;
    // Resetear el flag de lectura
    isReadingRef.current = false;
  }, []);

  /**
   * Connect to serial device
   */
  const connectSerialDevice = useCallback(async () => {
    console.log(`[${instanceId.current}] connectSerialDevice called`);
    console.log(`[${instanceId.current}] Current connection state: ${isSerialConnected ? 'CONNECTED' : 'DISCONNECTED'}`);
    const inicioConexion = Date.now();
    addLogBalanza('INFO', `Iniciando conexión (estado previo: ${isSerialConnected ? 'conectado' : 'desconectado'})`);

    try {
      if (!('serial' in navigator)) {
        // Mensaje por plataforma: en Android requiere Chrome 138+ y solo Bluetooth
        const msg = isAndroidDevice() ? SERIAL_UNSUPPORTED_ANDROID : SERIAL_UNSUPPORTED_DESKTOP;
        addLogBalanza('ERROR', `Web Serial NO disponible en este navegador → ${msg}`);
        setSerialError(msg);
        setIsSerialConnected(false);
        return;
      }

      // SOLUCIÓN 3: Limpiar timeouts de reinicio pendientes antes de conectar
      clearRestartTimeouts();
      // SOLUCIÓN 1: Asegurar que no haya una lectura en curso
      isReadingRef.current = false;

      console.log(`[${instanceId.current}] Requesting port selection...`);
      addLogBalanza('INFO', 'Abriendo el selector de puertos de Chrome…');
      const port = await (navigator as any).serial.requestPort();

      console.log(`[${instanceId.current}] Port selected, opening connection...`);
      addLogBalanza('INFO', 'Puerto seleccionado, abriendo a 9600 8N1…');
      await port.open({
        baudRate: 9600,
        dataBits: 8,
        stopBits: 1,
        parity: 'none',
        flowControl: 'none'
      });

      console.log(`[${instanceId.current}] Port opened successfully`);
      console.log(`[${instanceId.current}] Port readable:`, port.readable);
      console.log(`[${instanceId.current}] Port writable:`, port.writable);
      const infoPuerto = port.getInfo ? JSON.stringify(port.getInfo()) : 'n/d';
      addLogBalanza('INFO', `Puerto abierto en ${Date.now() - inicioConexion}ms · getInfo(): ${infoPuerto} · readable: ${!!port.readable} · writable: ${!!port.writable}`);

      if (port.writable) {
        writerRef.current = port.writable.getWriter();
        console.log(`[${instanceId.current}] Writer acquired for sending commands`);
        addLogBalanza('INFO', 'Writer adquirido');
      } else {
        addLogBalanza('WARN', 'El puerto NO tiene writable — no se podrán enviar los {RW}');
      }

      portRef.current = port;
      setIsSerialConnected(true);
      setSerialError(null);
      // Reset del watchdog de silencio para la nueva conexión
      consecutiveTimeoutsRef.current = 0;
      silenceWarnedRef.current = false;
      console.log(`[${instanceId.current}] ✓ Device connected successfully, starting reader...`);

      readFromPort();

      // SOLUCIÓN 2: Usar callbacksRef en lugar de serialOptions
      if (callbacksRef.current.autoRequestWeight) {
        startAutoWeightRequest();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to connect device';
      const rawError = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      // Siempre dejar el error crudo en el log: los matcheos por substring
      // de más abajo pueden no cubrir los textos de Chrome Android.
      addLogBalanza('ERROR', `Conexión falló → ${rawError}`);

      if (errorMessage.includes('No port selected by the user')) {
        console.log(`[${instanceId.current}] User cancelled port selection`);
        // En Android el caso más común es que la balanza no esté emparejada en
        // los Ajustes de Bluetooth del teléfono (Chrome no la lista). En desktop
        // el cancel es a propósito: mantener el return silencioso.
        setSerialError(isAndroidDevice() ? ANDROID_PAIRING_HINT : null);
        setIsSerialConnected(false);
        return;
      }

      let userFriendlyError = errorMessage;
      if (errorMessage.includes('Failed to open serial port')) {
        userFriendlyError = 'No se pudo abrir el puerto. Revisá que el dispositivo esté conectado y que no lo esté usando otra aplicación.';
      } else if (errorMessage.includes('not supported')) {
        userFriendlyError = errorMessage;
      } else {
        userFriendlyError = `Error de conexión: ${errorMessage}`;
      }

      console.error(`[${instanceId.current}] Connection error:`, errorMessage);
      setSerialError(userFriendlyError);
      setIsSerialConnected(false);
    }
  }, [readFromPort, isSerialConnected, startAutoWeightRequest, clearRestartTimeouts]);

  /**
   * Disconnect from serial device
   */
  const disconnectSerialDevice = useCallback(async () => {
    console.log(`[${instanceId.current}] disconnectSerialDevice called (manual)`);
    try {
      console.log(`[${instanceId.current}] Manually disconnecting device...`);
      addLogBalanza('INFO', 'Desconectando…');
      consecutiveTimeoutsRef.current = 0;
      silenceWarnedRef.current = false;

      setIsSerialConnected(false);
      stopAutoWeightRequest();

      if (readerRef.current) {
        try {
          await readerRef.current.cancel();
        } catch (e) {
          // Ignore errors
        }
        readerRef.current = null;
      }

      if (writerRef.current) {
        try {
          writerRef.current.releaseLock();
        } catch (e) {
          // Ignore errors
        }
        writerRef.current = null;
      }

      if (portRef.current) {
        try {
          await portRef.current.close();
        } catch (e) {
          // Ignore errors
        }
        portRef.current = null;
      }

      console.log(`[${instanceId.current}] ✓ Device disconnected successfully`);
    } catch (err) {
      console.error(`[${instanceId.current}] Disconnect error:`, err);
    }
  }, [stopAutoWeightRequest]);

  // Snapshot del entorno al montar: disponibilidad de Web Serial, contexto
  // seguro y plataforma — lo primero que hace falta para diagnosticar un
  // "en el celular no anda" sin chrome://inspect.
  useEffect(() => {
    const serialOk = typeof navigator !== 'undefined' && 'serial' in navigator;
    const seguro = typeof window !== 'undefined' && window.isSecureContext !== undefined
      ? window.isSecureContext
      : 'n/d';
    addLogBalanza(
      'INFO',
      `Web Serial: ${serialOk ? 'disponible' : 'NO disponible'} · contexto seguro: ${seguro} · plataforma: ${isAndroidDevice() ? 'Android' : 'desktop'}`
    );
  }, []);

  // Cleanup on provider unmount
  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      console.log(`[${instanceId.current}] DeviceProvider UNMOUNTING`);
      
      if (multipleIdsTimerRef.current) {
        clearTimeout(multipleIdsTimerRef.current);
      }
      stopAutoWeightRequest();

      if (readerRef.current) {
        readerRef.current.cancel().catch(() => {});
        readerRef.current = null;
      }

      if (writerRef.current) {
        try {
          writerRef.current.releaseLock();
        } catch (e) {
          // Ignore errors
        }
        writerRef.current = null;
      }

      if (portRef.current) {
        portRef.current.close().catch(() => {});
        portRef.current = null;
      }
    };
  }, [stopAutoWeightRequest]);

  /**
   * SOLUCIÓN 2: Wrapper para setSerialOptions que también actualiza callbacksRef
   * Esto asegura que processBuffer siempre tenga acceso a los callbacks más recientes
   */
  const updateSerialOptions = useCallback((options: UseSerialDevicesOptions) => {
    console.log(`[${instanceId.current}] Updating serial options and callbacks ref`);
    callbacksRef.current = options;
    setSerialOptions(options);
  }, []);

  const value: DeviceContextType = {
    isSerialConnected,
    serialError,
    connectSerialDevice,
    disconnectSerialDevice,
    sendSerialCommand,
    setSerialOptions: updateSerialOptions,  // SOLUCIÓN 2: Usar el wrapper que actualiza callbacksRef
    clearRestartTimeouts
  };

  return (
    <DeviceContext.Provider value={value}>
      {children}
    </DeviceContext.Provider>
  );
};

export const useDeviceContext = () => {
  const context = useContext(DeviceContext);
  if (context === undefined) {
    throw new Error('useDeviceContext must be used within a DeviceProvider');
  }
  return context;
};
